import { z } from 'zod';
import { defineEvent } from '../events.ts';
import { GuildEvent, PresenceActivity, Snowflake } from './base.ts';
import { CommandError } from './protocol.ts';

// Discord events the worker forwards (design §6.6, plan 1B Assumption 7). They're
// core's, under the reserved `discord` prefix, stored as domain events, so any
// module can subscribe. Member events from a reconcile carry `source: 'reconcile'`
// and go through the same code path as the gateway's.

export const MemberEventData = z.object({
  discordId: Snowflake,
  username: z.string().max(64),
  displayName: z.string().max(64),
  /** The avatar hash, or ''. */
  avatar: z.string().max(128),
  /** Guild role ids, without @everyone. Empty for a member who left. */
  roles: z.array(Snowflake),
  /** In membership screening. */
  pending: z.boolean(),
  source: z.enum(['gateway', 'reconcile']),
});
export type MemberEventData = z.infer<typeof MemberEventData>;

/**
 * An update also says what the roles were before, when the worker knew. `previousRoles` is absent when
 * it did not (a member it had not cached, or only partly): absent means unknown, never "none". An empty
 * list is a member who really had no roles.
 */
export const MemberUpdatedData = MemberEventData.extend({ previousRoles: z.array(Snowflake).optional() });
export type MemberUpdatedData = z.infer<typeof MemberUpdatedData>;

export const MemberJoined = defineEvent({ type: 'discord.member.joined', data: MemberEventData });
export const MemberUpdated = defineEvent({ type: 'discord.member.updated', data: MemberUpdatedData });
export const MemberLeft = defineEvent({ type: 'discord.member.left', data: MemberEventData });

/** A member joined, left or changed voice channel (not mute or deafen). */
export const VoiceMoved = defineEvent({
  type: 'discord.voice.moved',
  data: z.object({ discordId: Snowflake, fromChannelId: Snowflake.nullable(), toChannelId: Snowflake.nullable() }),
});

export const GuildEventCreated = defineEvent({ type: 'discord.guild_event.created', data: GuildEvent });
export const GuildEventUpdated = defineEvent({ type: 'discord.guild_event.updated', data: GuildEvent });
export const GuildEventDeleted = defineEvent({ type: 'discord.guild_event.deleted', data: GuildEvent });

/** Someone clicked Interested on a scheduled event, or took it back. */
const GuildEventUser = z.object({ eventId: Snowflake, discordId: Snowflake });
export const GuildEventUserAdded = defineEvent({ type: 'discord.guild_event.user_added', data: GuildEventUser });
export const GuildEventUserRemoved = defineEvent({ type: 'discord.guild_event.user_removed', data: GuildEventUser });

/**
 * A thread changed: archived, unarchived, or renamed or otherwise edited (plan 5D). Ids and the
 * archive flag only. `parentId` is the channel it is in.
 */
export const ThreadUpdated = defineEvent({
  type: 'discord.thread.updated',
  data: z.object({ threadId: Snowflake, parentId: Snowflake, archived: z.boolean() }),
});

/** A thread was deleted. The worker only hears of one it had cached. */
export const ThreadDeleted = defineEvent({
  type: 'discord.thread.deleted',
  data: z.object({ threadId: Snowflake, parentId: Snowflake }),
});

/**
 * Someone posted in a thread (plan 5D). Never the text, the attachments or the mentions:
 * who posted, in which thread, and the message's id. Messages outside threads are not forwarded.
 */
export const ThreadMessage = defineEvent({
  type: 'discord.thread.message',
  data: z.object({ threadId: Snowflake, parentId: Snowflake, authorDiscordId: Snowflake, authorBot: z.boolean(), messageId: Snowflake }),
});

/**
 * A member's status or games changed (plan 6A). Sent only when the worker has the presence intent
 * (ITC_DISCORD_PRESENCE), only when the status or the set of games differs from what it last sent
 * for them, and never for a bot. The fields are those `guild.activity` reports. Not sent while the
 * server is away, so consumers take it as a hint and also read `guild.activity` on a timer. Kept 2 days.
 */
export const PresenceUpdated = defineEvent({ type: 'discord.presence.updated', data: PresenceActivity, retentionDays: 2 });

/** What a deleted channel was: announcement channels count as text; forums and the rest are `other`. */
export const DeletedChannelKind = z.enum(['voice', 'stage', 'text', 'category', 'other']);
export type DeletedChannelKind = z.infer<typeof DeletedChannelKind>;

/**
 * A channel of the guild was deleted (plan 6A). Threads have their own event. `parentId` is its
 * category, null for a category or a channel in none.
 */
export const ChannelDeleted = defineEvent({
  type: 'discord.channel.deleted',
  data: z.object({ channelId: Snowflake, parentId: Snowflake.nullable(), kind: DeletedChannelKind }),
});

/** Every event type a worker may forward. */
export const DISCORD_EVENTS = [
  MemberJoined,
  MemberUpdated,
  MemberLeft,
  VoiceMoved,
  GuildEventCreated,
  GuildEventUpdated,
  GuildEventDeleted,
  GuildEventUserAdded,
  GuildEventUserRemoved,
  ThreadUpdated,
  ThreadDeleted,
  ThreadMessage,
  PresenceUpdated,
  ChannelDeleted,
] as const;

/** Why a user lost access to ITC Web. */
export const AccessLostReason = z.enum(['left', 'gate_role_lost', 'pending']);
export type AccessLostReason = z.infer<typeof AccessLostReason>;

/**
 * A user lost access through Discord: their sessions are revoked in the same
 * transaction. Notifying admins and closing live streams subscribe to it (1C).
 */
export const UserAccessLost = defineEvent({
  type: 'core.user.access_lost',
  data: z.object({ userId: z.string(), discordId: Snowflake, reason: AccessLostReason }),
});

/** The terminal statuses `core.command.finished` reports. */
export const FinishedStatus = z.enum(['done', 'failed', 'expired', 'unknown']);
export type FinishedStatus = z.infer<typeof FinishedStatus>;

/**
 * A Discord command ended (plan 1C, Assumption 2): `done`, `failed`, `expired`, or
 * `unknown` (its worker went away mid-command). Published in the same transaction as
 * the status change, and delivered only to subscribers of the module that staged the
 * command (`module`); core's subscribers get core's commands.
 *
 * A command can finish more than once: an `unknown` one can be followed by a late
 * `done` or `failed`, and a retried one finishes again with its new `attempts`.
 * Handlers must take each event as the latest word, not the only one.
 */
export const CommandFinished = defineEvent({
  type: 'core.command.finished',
  data: z.object({
    commandId: z.string(),
    capability: z.string(),
    /** The module that staged the command. */
    module: z.string(),
    /** The key it was staged with, or ''. */
    key: z.string(),
    status: FinishedStatus,
    attempts: z.number().int(),
    /** Why it didn't succeed; null when `done`. */
    error: CommandError.nullable(),
    /** The parsed result, only when the capability stores it (`storeResult`) and it fits in 256 KB; otherwise null. */
    result: z.unknown(),
  }),
});
export type CommandFinished = z.infer<typeof CommandFinished.data>;
